import type { Logger } from "@expert/logging";

import type { SelectedOfferReplacement } from "../stores/useProductOfferStore";

import { EligibilityStatus, type EligibilityRequest, type EligibleProduct } from "../types";

export interface EligibilityResultLogFields {
    eligibleCount: number;
    ineligibleCount: number;
    alreadyEnrolledCount: number;
    totalCount: number;
    partner: string;
    isPrecheck: boolean;
    sessionId: string;
    productSkusReturned: string[];
}

export const buildEligibilityResultLog = (
    eligibility: EligibilityRequest,
    productConfigs: EligibleProduct[],
): EligibilityResultLogFields => {
    let eligibleCount = 0;
    let ineligibleCount = 0;
    let alreadyEnrolledCount = 0;
    const productSkusReturned: string[] = [];

    for (const product of productConfigs) {
        productSkusReturned.push(product.productSku);
        if (product.status === EligibilityStatus.Eligible) eligibleCount++;
        else if (product.status === EligibilityStatus.Enrolled) alreadyEnrolledCount++;
        else ineligibleCount++;
    }

    return {
        eligibleCount,
        ineligibleCount,
        alreadyEnrolledCount,
        totalCount: eligibleCount + ineligibleCount + alreadyEnrolledCount,
        partner: eligibility.partner,
        isPrecheck: !!eligibility.eligibilityAttributes?.isPrecheck,
        sessionId: eligibility.sessionId,
        productSkusReturned,
    };
};

/**
 * Logs a re-check that moved the selected product to a new offer id (for expert-sales, a new USP
 * quoteId — USP invalidates the old one). Shared by both `updateProducts` callers so the log reads
 * the same whichever path triggered the re-check. No-op when the update replaced nothing.
 */
export const logSelectedOfferReplacement = (
    logger: Logger,
    sessionId: string,
    replacement: SelectedOfferReplacement | undefined,
) => {
    if (!replacement) return;
    logger.info({ sessionId, ...replacement }, "Selected offer quoteId refreshed after eligibility re-check");
};

/**
 * Logs an eligibility response that landed out of order and was not applied: the store already holds
 * products from a newer request, which invalidated this one's USP quotes.
 */
export const logSupersededEligibilityResult = (logger: Logger, sessionId: string, requestedAt: number | undefined) => {
    logger.info({ sessionId, requestedAt }, "Ignored superseded eligibility result");
};
